import { useCallback, useEffect, useState } from "react";
import {
  getNotificationPermission,
  requestNotificationPermission,
  subscribeNotificationPermission,
} from "utils/firebaseMessaging";

/**
 * Tracks Notification.permission and requests it from a user gesture.
 * Firefox will not show a prompt (and will not grant) if asked on page load.
 *
 * @returns {{ permission: NotificationPermission|"", requestPermission: () => Promise<NotificationPermission|""> }}
 */
const useChatNotificationPermission = () => {
  const [permission, setPermission] = useState(getNotificationPermission);

  const requestPermission = useCallback(async () => {
    const next = await requestNotificationPermission();
    setPermission(next || getNotificationPermission());
    return next;
  }, []);

  useEffect(() => {
    return subscribeNotificationPermission((next) => {
      setPermission(next || getNotificationPermission());
    });
  }, []);

  useEffect(() => {
    const sync = () => setPermission(getNotificationPermission());
    document.addEventListener("visibilitychange", sync);
    window.addEventListener("focus", sync);
    return () => {
      document.removeEventListener("visibilitychange", sync);
      window.removeEventListener("focus", sync);
    };
  }, []);

  useEffect(() => {
    if (permission !== "default") return undefined;

    const onGesture = () => {
      void requestPermission();
    };

    document.addEventListener("click", onGesture, { once: true, capture: true });
    document.addEventListener("keydown", onGesture, { once: true, capture: true });
    return () => {
      document.removeEventListener("click", onGesture, { capture: true });
      document.removeEventListener("keydown", onGesture, { capture: true });
    };
  }, [permission, requestPermission]);

  return { permission, requestPermission };
};

export default useChatNotificationPermission;
